import { FC, useMemo } from 'react';

import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import {
  GiftItemInfoData,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import {
  GIFT_BEST_MAX,
  arrangeGiftBestFirst,
  hasGiftBestTag,
} from '@shared/utils/giftBestTag.util';

const NO_SUB = '';

const categoryIdOf = (item: GiftItemInfoData): string | undefined =>
  typeof item.giftDeliveryCategory === 'object'
    ? item.giftDeliveryCategory?._id
    : item.giftDeliveryCategory ?? undefined;

interface BestGroup {
  key: string;
  categoryName: string;
  subCategory: string;
  total: number; // 그 분류의 판매중 배송형 상품 수
  best: GiftItemInfoData[]; // 판매 화면에서 그 분류 맨 앞에 놓이는 순서(가격 낮은 순)
  overflow: GiftItemInfoData[]; // 태그는 있지만 6개를 넘어 일반 상품으로 취급되는 것
}

/** 상품이 속한 BEST 단위(카테고리 + 하위분류) 키. 현황판·개수 제한 공용 */
export const giftBestGroupKey = (
  item: Pick<GiftItemInfoData, 'giftDeliveryCategory' | 'subCategory'>,
  categories: GiftDeliveryCategoryData[],
): string => {
  const categoryId = categoryIdOf(item as GiftItemInfoData) ?? '';
  const known = categories.find((c) => c._id === categoryId)?.subCategories ?? [];
  const sub = item.subCategory && known.includes(item.subCategory) ? item.subCategory : NO_SUB;
  return `${categoryId}:${sub}`;
};


/**
 * BEST 현황판 — BEST는 하위분류 단위다. 판매 화면의 각 하위분류 구역 맨 앞에 무엇이 올라가 있는지,
 * 어느 분류가 채워지고 어느 분류가 비었는지, 가격대가 어떻게 퍼져 있는지 보면서 고르게 한다.
 *
 * - focus를 주면(원상품 수정 화면) 그 상품이 속한 분류를 강조한다.
 * - 판매 화면과 같은 규칙(arrangeGiftBestFirst): 판매중 배송형 + `베스트` 태그, 분류마다 표시 순서 빠른 6개.
 */
export const GiftBestBoard: FC<{
  items: GiftItemInfoData[];
  categories: GiftDeliveryCategoryData[];
  focus?: { categoryId?: string; subCategory?: string; itemId?: string };
  onRemove?: (item: GiftItemInfoData) => void;
  busyId?: string | null;
}> = ({ items, categories, focus, onRemove, busyId }) => {
  const groups = useMemo(() => {
    const selling = items
      .filter((item) => item.type === GiftItemInfoType.Delivery && item.isEnabled !== false)
      .sort(
        (a, b) =>
          (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER),
      );
    const result: BestGroup[] = [];
    categories.forEach((category) => {
      const subs = category.subCategories ?? [];
      [...subs, NO_SUB].forEach((sub) => {
        const key = `${category._id}:${sub}`;
        const members = selling.filter(
          (item) => giftBestGroupKey(item, categories) === key,
        );
        if (members.length === 0) return;
        const { best } = arrangeGiftBestFirst(
          members,
          (item) => item.tags,
          (item) => item.priceRetail || 0,
        );
        const bestSet = new Set(best);
        result.push({
          key,
          categoryName: category.name,
          subCategory: sub,
          total: members.length,
          best,
          overflow: members.filter(
            (item) => !bestSet.has(item) && hasGiftBestTag(item.tags),
          ),
        });
      });
    });
    return result;
  }, [items, categories]);

  const filled = groups.filter((group) => group.best.length > 0).length;

  return (
    <section
      aria-label="BEST 현황"
      className="rounded-md border border-amber-200 bg-amber-50/60 px-5 py-4"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-sm font-semibold text-gray-900">
          하위분류별 BEST{' '}
          <span className="text-amber-700">
            {filled}/{groups.length}개 분류 지정
          </span>
        </h2>
        <p className="text-xs text-gray-500">
          판매 화면의 각 하위분류 맨 앞에 가격 낮은 순으로 노출 · 분류당 최대 {GIFT_BEST_MAX}개
          · 전 상조 공통
        </p>
      </div>
      <ul className="mt-3 grid gap-x-6 gap-y-2 md:grid-cols-2 xl:grid-cols-3">
        {groups.map((group) => {
          const focused =
            !!focus?.categoryId &&
            group.key === `${focus.categoryId}:${focus.subCategory ?? NO_SUB}`;
          return (
            <li
              key={group.key}
              className={`min-w-0 rounded px-2 py-1.5 ${
                focused ? 'bg-white ring-1 ring-amber-300' : ''
              }`}
            >
              <p className="text-xs text-gray-500">
                {group.categoryName}
                {group.subCategory ? ` › ${group.subCategory}` : ''}{' '}
                <span className="text-gray-400">
                  {group.total}개 중 BEST {group.best.length}/{GIFT_BEST_MAX}
                </span>
              </p>
              {group.overflow.length > 0 && (
                <p className="mt-0.5 text-xs text-red-600">
                  {GIFT_BEST_MAX}개 초과 — 표시 순서가 늦은 {group.overflow.length}개(흐리게)는
                  BEST로 안 나옵니다
                </p>
              )}
              {group.best.length === 0 ? (
                <p className="mt-0.5 text-xs text-gray-400">BEST 없음</p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {[...group.best, ...group.overflow].map((item, index) => (
                    <li
                      key={item._id}
                      className={`flex min-w-0 items-center gap-2 text-sm ${
                        index < group.best.length ? '' : 'opacity-40'
                      }`}
                    >
                      {item.image && typeof item.image === 'object' ? (
                        <img
                          src={fileBucketUrl(item.image.url, 'raw')}
                          alt=""
                          className="h-7 w-7 shrink-0 rounded border border-gray-200 object-cover"
                        />
                      ) : (
                        <span className="h-7 w-7 shrink-0 rounded border border-gray-200 bg-white" />
                      )}
                      <span
                        className={`min-w-0 flex-1 truncate ${
                          item._id === focus?.itemId ? 'font-semibold' : ''
                        }`}
                      >
                        {item.name}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-gray-600">
                        {(item.priceRetail || 0).toLocaleString()}원
                      </span>
                      {onRemove && (
                        <button
                          type="button"
                          onClick={() => onRemove(item)}
                          disabled={busyId === item._id}
                          aria-label={`${item.name} BEST 해제`}
                          className="shrink-0 rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-white hover:text-red-600 disabled:opacity-40"
                        >
                          해제
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      {groups.length === 0 && (
        <p className="mt-2 text-xs text-gray-500">판매중인 배송형 원상품이 없습니다.</p>
      )}
    </section>
  );
};
